import { STAGE_LABELS, type CaseStage } from '../../lib/caseStage';

/**
 * "Pizza tracker" for case progress. StageTracker is the full stepper for
 * the case view; StageMiniBar is the six-segment strip for queue rows.
 * Both are pure renderings of a derived CaseStage — no state, no fetches.
 */

type DotState = 'done' | 'current' | 'stalled' | 'skipped' | 'future';

function dotStateFor(i: number, stage: CaseStage): DotState {
  if (i === 3 && stage.mediationSkipped) return 'skipped';
  if (i < stage.index) return 'done';
  if (i > stage.index) return 'future';
  return stage.stalled ? 'stalled' : 'current';
}

const DOT_CLASSES: Record<DotState, string> = {
  done: 'border-brand bg-brand text-white',
  current: 'border-brand bg-brand-subtle text-brand ring-2 ring-brand/25',
  stalled: 'border-amber-500 bg-amber-100 text-amber-700 ring-2 ring-amber-300/50',
  skipped: 'border-dashed border-gray-300 bg-white text-gray-300',
  future: 'border-surface-border bg-white text-gray-300',
};

export function StageTracker({ stage }: { stage: CaseStage }) {
  const n = STAGE_LABELS.length;
  // The track runs between the CENTERS of the first and last grid cells
  // (each cell is 100/n % wide), so connectors meet the dots exactly.
  const inset = `${100 / (2 * n)}%`;
  const fillPct = (stage.index / (n - 1)) * 100;
  return (
    <div className="card overflow-x-auto p-4">
      <div className="relative min-w-[560px]">
        <div
          className="absolute top-[13px] h-0.5 bg-surface-border"
          style={{ left: inset, right: inset }}
        >
          <div
            className={['h-full', stage.stalled ? 'bg-amber-400' : 'bg-brand'].join(' ')}
            style={{ width: `${fillPct}%` }}
          />
        </div>
        <ol className="relative grid grid-cols-6">
          {STAGE_LABELS.map((label, i) => {
            const state = dotStateFor(i, stage);
            const isCurrent = state === 'current' || state === 'stalled';
            return (
              <li key={label} className="flex flex-col items-center">
                <div
                  className={[
                    'flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-semibold',
                    DOT_CLASSES[state],
                  ].join(' ')}
                  title={state === 'skipped' ? 'Resolved without a mediation session' : label}
                >
                  {state === 'done' ? '✓' : state === 'stalled' ? '!' : state === 'skipped' ? '—' : i + 1}
                </div>
                <div
                  className={[
                    'mt-1.5 text-center text-[11px] font-medium',
                    isCurrent ? (stage.stalled ? 'text-amber-700' : 'text-brand') : state === 'done' ? 'text-gray-600' : 'text-gray-400',
                  ].join(' ')}
                >
                  {label}
                  {isCurrent && stage.sublabel && (
                    <div className="font-normal text-gray-500">{stage.sublabel}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

/** Compact strip for queue rows: six segments + the stage name. */
export function StageMiniBar({ stage }: { stage: CaseStage }) {
  const title = `Stage ${stage.index + 1} of ${STAGE_LABELS.length}: ${stage.label}${stage.sublabel ? ` (${stage.sublabel})` : ''}`;
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      <span className="flex gap-0.5">
        {STAGE_LABELS.map((label, i) => {
          const state = dotStateFor(i, stage);
          const tone =
            state === 'done'
              ? 'bg-brand'
              : state === 'current'
                ? 'bg-brand/60'
                : state === 'stalled'
                  ? 'bg-amber-400'
                  : 'bg-surface-border';
          return <span key={label} className={`h-1.5 w-2.5 rounded-sm ${tone}`} />;
        })}
      </span>
      <span className={stage.stalled ? 'font-medium text-amber-700' : 'text-gray-600'}>
        {stage.label}
        {stage.sublabel ? ` · ${stage.sublabel}` : ''}
      </span>
    </span>
  );
}
